import { useEffect, useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import { getSkillProgress } from '../graphQLOperations/SingleUnit';
import { focusRoadmapSkills, focusUnit } from '../graphQLOperations/Focus';
import { splitId } from '../businessLogicService/splitIdsFromBackEnd';
import { FocusRoadmapSkillsQuery } from '../graphQLOperations/__generated__/FocusRoadmapSkillsQuery.graphql';
import { FocusUnitQuery } from '../graphQLOperations/__generated__/FocusUnitQuery.graphql';
import { SingleUnitSkillProgressQuery } from '../graphQLOperations/__generated__/SingleUnitSkillProgressQuery.graphql';
import { TargetSkillTypeLabel } from '../models/TargetSkillType';
import { ExclusionReason } from '../models/UnitFocus';
import {
  ProgressStudent,
  SkillProgressForUnit,
  resolveEligibilityFromProgress,
  resolveEligibilityForUnits,
} from '../businessLogicService/focusService';

export interface UnitFocusEligibility {
  included: ProgressStudent[];
  excluded: { student: ProgressStudent; reason: ExclusionReason }[];
  roadmapLabel: string;
  unitName: string;
  loading: boolean;
}

const EMPTY_ELIGIBILITY: UnitFocusEligibility = {
  included: [],
  excluded: [],
  roadmapLabel: '',
  unitName: '',
  loading: false,
};

type CoreUnit = Pick<NonNullable<FocusUnitQuery['response']['target']>, 'targetSkills'>;

export function coreUnitSkillIds(unitData: CoreUnit | null | undefined): string[] {
  return (unitData?.targetSkills ?? [])
    .filter(ts => ts?.targetSkillType?.name === TargetSkillTypeLabel.TARGET)
    .map(ts => ts?.skill?.id)
    .filter((id): id is string => !!id);
}

export function normalizeSkillProgress(
  progressData: SingleUnitSkillProgressQuery['response'] | null | undefined,
): SkillProgressForUnit[] {
  return (progressData?.skillProgress ?? []).map(sp => ({
    skillId: sp.skill?.id ?? '',
    buckets: (sp.usersBySkillResults ?? []).map(bucket => ({
      result: bucket.key,
      students: (bucket.value ?? []).map(user => ({
        id: user.id,
        name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
        publicUserId: user.publicUserId ?? '',
      })),
    })),
  }));
}

export function useUnitFocusEligibility(
  visible: boolean,
  targetId: string,
  classIds: string[],
  roadmapMemberIds?: ReadonlySet<string> | null,
): UnitFocusEligibility {
  const environment = useRelayEnvironment();
  const [state, setState] = useState<UnitFocusEligibility>(EMPTY_ELIGIBILITY);
  const classKey = classIds.join(',');

  useEffect(() => {
    const ids = classKey ? classKey.split(',') : [];
    if (!visible || !targetId || ids.length === 0) {
      setState(EMPTY_ELIGIBILITY);
      return;
    }
    if (roadmapMemberIds === null) {
      setState(prev => ({ ...prev, loading: true }));
      return;
    }

    let cancelled = false;
    setState(prev => ({ ...prev, loading: true }));

    Promise.all([
      fetchQuery<FocusUnitQuery>(environment, focusUnit, { targetId }, { fetchPolicy: 'store-or-network' }).toPromise(),
      fetchQuery<SingleUnitSkillProgressQuery>(environment, getSkillProgress, {
        classIds: ids,
        targetIds: [targetId],
      }).toPromise(),
    ])
      .then(([unitData, progressData]) => {
        if (cancelled) return;
        if (!unitData?.target) {
          setState(EMPTY_ELIGIBILITY);
          return;
        }
        const { included, excluded } = resolveEligibilityFromProgress(
          coreUnitSkillIds(unitData.target),
          normalizeSkillProgress(progressData),
          roadmapMemberIds ?? undefined,
        );
        setState({
          included,
          excluded,
          roadmapLabel: unitData?.target?.targetGroup?.name ?? '',
          unitName: unitData?.target?.name ?? '',
          loading: false,
        });
      })
      .catch(() => {
        if (!cancelled) setState(EMPTY_ELIGIBILITY);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, targetId, classKey, roadmapMemberIds, environment]);

  return state;
}

export interface RoadmapUnitCompletion {
  completedUnitIds: Set<string>;
  loading: boolean;
  error: boolean;
}

const EMPTY_COMPLETION: RoadmapUnitCompletion = { completedUnitIds: new Set(), loading: false, error: false };

export function useRoadmapUnitCompletion(
  enabled: boolean,
  roadmapId: string,
  unitIds: string[],
  classIds: string[],
  roadmapMemberIds?: ReadonlySet<string> | null,
): RoadmapUnitCompletion {
  const environment = useRelayEnvironment();
  const [state, setState] = useState<RoadmapUnitCompletion>(EMPTY_COMPLETION);
  const unitKey = unitIds.join(',');
  const classKey = classIds.join(',');

  useEffect(() => {
    const units = unitKey ? unitKey.split(',') : [];
    const classes = classKey ? classKey.split(',') : [];
    if (!enabled || !roadmapId || units.length === 0 || classes.length === 0) {
      setState(EMPTY_COMPLETION);
      return;
    }
    if (roadmapMemberIds === null) {
      setState(prev => ({ ...prev, loading: true }));
      return;
    }

    let cancelled = false;
    setState(prev => ({ ...prev, loading: true, error: false }));

    Promise.all([
      fetchQuery<SingleUnitSkillProgressQuery>(environment, getSkillProgress, {
        classIds: classes,
        targetIds: units,
      }).toPromise(),
      fetchQuery<FocusRoadmapSkillsQuery>(
        environment,
        focusRoadmapSkills,
        { targetGroupId: Number(splitId(roadmapId)) },
        { fetchPolicy: 'store-or-network' },
      ).toPromise(),
    ])
      .then(([progressData, roadmapData]) => {
        if (cancelled) return;
        const byId = new Map(
          (roadmapData?.targetGroup?.targets ?? []).flatMap(unit => (unit ? [[unit.id, unit] as const] : [])),
        );
        if (units.some(id => !byId.has(id))) {
          setState({ completedUnitIds: new Set(), loading: false, error: true });
          return;
        }
        const progress = normalizeSkillProgress(progressData);
        const members = roadmapMemberIds ?? undefined;
        const eligibility = resolveEligibilityForUnits(
          units.map(id => coreUnitSkillIds(byId.get(id))),
          progress,
          members,
        );
        const completedUnitIds = new Set<string>();
        eligibility.forEach(({ included, excluded }, i) => {
          if (included.length === 0 && excluded.length > 0) completedUnitIds.add(units[i]);
        });
        setState({ completedUnitIds, loading: false, error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ completedUnitIds: new Set(), loading: false, error: true });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, roadmapId, unitKey, classKey, roadmapMemberIds, environment]);

  return state;
}
